import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useRef,
  useState,
} from 'react';
import { CheckboxFilter } from './filters/TableCheckboxFilter';
import { TableCellProps, TableProps } from '@mui/material';
import { TextFilter } from './filters/TableTextFilter';

export interface HeadCell extends TableCellProps {
  disablePadding?: boolean;
  name: string;
  sort?: string;
  label: any;
  icon?: boolean;
  align?: 'left' | 'center' | 'right' | 'inherit' | 'justify' | undefined;
  // tslint:disable-next-line:no-any
  component?: any;
  render?: (row: any, name?: any) => {};
  hover?: boolean;
  checkboxFilter?: string;
  textFilter?: string;
  clearFilter?: boolean;
}

export type Order = 'asc' | 'desc';

export interface RowCell {
  id: number;
  icon?: any;
  [n: string]: any;
  color?: string;
}
export type IndexTableProps = {
  checkboxes?: boolean;
  expandOnCheckbox?: boolean;
  icons?: boolean;
  iconHead?: any;
  isLoading?: boolean;
  headCells: HeadCell[];
  additionalHeadCells?: HeadCell[];
  rows: RowCell[];
  title: string;
  visible?: boolean;
  onRowClick?: (event: React.MouseEvent<unknown>, row: RowCell) => void;
  defaultOrder?: Order;
  defaultOrderBy?: string;
  toolbar?: boolean;
  hoverCell?: boolean;
  exportFormat?: (rows: RowCell[]) => any;
  //NEED TO REMOVE CAUSING LOOP OF RENDERS
  selected?: readonly number[];
  setSelected?: React.Dispatch<React.SetStateAction<readonly number[]>>;
  //////
  selectMultiple?: boolean;
  hideclearFilters?: boolean;
  pagination?: boolean;
  itemBorder?: boolean;
  rowBorder?: Object;
  tableProps?: TableProps;
  defaultRowsPerPage?: number;
};

export type IndexTableContextContent = {
  handleRequestSort: (
    event: React.MouseEvent<unknown>,
    property: string
  ) => void;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  clearFilters: () => void;
  updateCheckboxFilters: (
    filterName: string,
    key: string,
    value: boolean
  ) => void;
  selectAllCheckboxFilters: (filterName: string) => void;
  unselectAllCheckboxFilters: (filterName: string) => void;
  updateTextFilters: (filterName: string, value: string) => void;
  definedRowsPerPageOptions: {
    value: any;
    label: string;
  }[];
  emptyRows: number;
  page: number;
  setPage: Function;
  setDefaultCheckboxFilters: Function;
  setDefaultTextFilters: Function;
  setCheckboxFilters: Function;
  setTextFilters: Function;
  textFilters: TextFilter;
  checkboxFilters: CheckboxFilter;
  filteredData: RowCell[];
  setFilteredData: Function;
  order: Order;
  setOrder: Dispatch<SetStateAction<Order>>;
  orderBy: string;
  setOrderBy: Dispatch<SetStateAction<string>>;
  rowsPerPage: number;
  setRowsPerPage: Function;
  defaultCheckboxFilters: CheckboxFilter;
} & IndexTableProps;

export const IndexTableContext = createContext<IndexTableContextContent>({
  headCells: [],
  rows: [],
  title: '',
  handleRequestSort: () => {},
  handleChangePage: () => {},
  handleChangeRowsPerPage: () => {},
  handleSelectAllClick: () => {},
  clearFilters: () => {},
  updateCheckboxFilters: () => {},
  selectAllCheckboxFilters: () => {},
  unselectAllCheckboxFilters: () => {},
  updateTextFilters: () => {},
  definedRowsPerPageOptions: [],
  emptyRows: 0,
  page: 0,
  setPage: () => {},
  setDefaultCheckboxFilters: () => {},
  setDefaultTextFilters: () => {},
  setCheckboxFilters: () => {},
  setTextFilters: () => {},
  textFilters: {},
  checkboxFilters: {},
  filteredData: [],
  setFilteredData: () => {},
  order: 'asc',
  setOrder: () => {},
  orderBy: '',
  setOrderBy: () => {},
  rowsPerPage: 25,
  setRowsPerPage: () => {},
  defaultCheckboxFilters: {},
});

export const IndexTableProvider = ({
  checkboxes,
  expandOnCheckbox,
  icons,
  iconHead,
  isLoading = false,
  visible,
  onRowClick,
  defaultOrder = 'asc',
  defaultOrderBy = '',
  toolbar,
  hoverCell,
  exportFormat,
  //NEED TO REMOVE CAUSING LOOP OF RENDERS
  selected: controlSelected,
  setSelected: controlSetSelected,
  //////
  selectMultiple,
  pagination = true,
  itemBorder,
  rowBorder,
  defaultRowsPerPage,
  headCells,
  additionalHeadCells,
  rows,
  title,
  children,
  hideclearFilters = false,
  tableProps,
}: IndexTableProps & { children: any }) => {
  //local states
  /*****************************************************************/
  const [order, setOrder] = useState<Order>(defaultOrder);
  const [orderBy, setOrderBy] = useState<string>(defaultOrderBy);
  const [uncontrolSelected, uncontrolSetSelected] = useState<readonly number[]>(
    []
  );
  const selected = controlSelected || uncontrolSelected;
  const setSelected = controlSetSelected || uncontrolSetSelected;
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    defaultRowsPerPage || 25
  );
  const [defaultCheckboxFilters, setDefaultCheckboxFilters] =
    useState<CheckboxFilter>({});
  const [checkboxFilters, setCheckboxFilters] = useState<CheckboxFilter>({});
  const [defaultTextFilters, setDefaultTextFilters] = useState<TextFilter>({});
  const [textFilters, setTextFilters] = useState<TextFilter>({});
  const [filteredData, setFilteredData] = useState<RowCell[]>([]);
  /*****************************************************************/

  //local functions
  /*****************************************************************/
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'asc' : 'desc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = filteredData.map(n => n.id);
      setSelected(newSelecteds);
      return;
    }

    setSelected([]);
  };

  const clearFilters = () => {
    setCheckboxFilters(JSON.parse(JSON.stringify(defaultCheckboxFilters)));
    setTextFilters(JSON.parse(JSON.stringify(defaultTextFilters)));
  };

  // const onDelete = () => {
  //   if (deleteFunction) {
  //     deleteFunction(selected);
  //   }
  // };

  const updateCheckboxFilters = (
    filterName: string,
    key: string,
    value: boolean
  ) => {
    checkboxFilters[filterName][key] = value;

    setCheckboxFilters(JSON.parse(JSON.stringify(checkboxFilters)));
  };

  const selectAllCheckboxFilters = (filterName: string) => {
    checkboxFilters[filterName] = { ...defaultCheckboxFilters[filterName] };
    setCheckboxFilters(JSON.parse(JSON.stringify(checkboxFilters)));
  };

  const unselectAllCheckboxFilters = (filterName: string) => {
    const keys = Object.keys(checkboxFilters[filterName]);

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      checkboxFilters[filterName][key] = false;
    }

    setCheckboxFilters(JSON.parse(JSON.stringify(checkboxFilters)));
  };

  const updateTextFilters = (filterName: string, value: string) => {
    textFilters[filterName] = value;

    setTextFilters(JSON.parse(JSON.stringify(textFilters)));
  };

  //local variables
  /*****************************************************************/
  const definedRowsPerPageOptions = [
    {
      value: 5,
      label: '5',
    },
    {
      value: 10,
      label: '10',
    },
    {
      value: 25,
      label: '25',
    },
    {
      value: filteredData.length,
      label: 'All',
    },
  ];

  // Avoid a layout jump when reaching the last page with empty props.rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
  /*****************************************************************/
  return (
    <IndexTableContext.Provider
      value={{
        checkboxes,
        expandOnCheckbox,
        icons,
        iconHead,
        isLoading,
        visible,
        onRowClick,
        defaultOrder,
        defaultOrderBy,
        toolbar,
        hoverCell,
        exportFormat,
        //NEED TO REMOVE CAUSING LOOP OF RENDERS
        selected,
        setSelected,
        //////
        selectMultiple,
        pagination,
        itemBorder,
        rowBorder,
        headCells,
        rows,
        title,
        handleRequestSort,
        handleChangePage,
        handleChangeRowsPerPage,
        handleSelectAllClick,
        clearFilters,
        updateCheckboxFilters,
        selectAllCheckboxFilters,
        unselectAllCheckboxFilters,
        updateTextFilters,
        definedRowsPerPageOptions,
        emptyRows,
        page,
        setPage,
        setDefaultCheckboxFilters,
        setDefaultTextFilters,
        setCheckboxFilters,
        setTextFilters,
        textFilters,
        checkboxFilters,
        filteredData,
        setFilteredData,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        rowsPerPage,
        setRowsPerPage,
        defaultCheckboxFilters,
        hideclearFilters,
        tableProps,
        additionalHeadCells,
      }}
    >
      {children}
    </IndexTableContext.Provider>
  );
};

export const useIndexTableContext = () => useContext(IndexTableContext);
